import type {
  CategoryAdmission,
  ContractAction,
  ContractNeed,
  ContractorStatus,
  ContractStatus,
  ContractAmendRow,
  ContractRow,
  ContractTermsView,
  ContractType,
  PaymentKind,
  Purpose,
  WorkCategory,
  WorkOption,
} from '../api';
import { plural } from '../calendar/repeat';
import type { ContractTab } from '../nav/routes';
import { guestButtons, ukGuestButtons } from './guest';
import type { ContractTableRow } from './ContractTable';
import type { DropdownOption } from '../components/Dropdown';

/*
 * Правила показа договоров: подписи статуса, тон светофора, деньги, налог,
 * сроки и допуск по предмету. Вынесено из компонентов, чтобы проверять без
 * разметки.
 *
 * Подписи повторяют серверные (server/src/contracts/contracts.model.ts):
 * строка реестра и карточка приносят готовый `statusLabel`, а вкладка и
 * форма показывают статусы, которых в ответе ещё нет.
 *
 * Правила заведения повторяют серверные из
 * `server/src/contracts/intake.model.ts` слово в слово: форма обязана
 * говорить то же самое, что запретит сервер, и говорить это до отправки.
 */

/**
 * Название первого места: им зовётся сам текст договора. То же слово, что и у
 * сервера (`FIRST_PLACE` в contracts.service.ts), — им сервер называет место,
 * когда форма названия не прислала.
 */
export const FIRST_PLACE = 'Договор';

/** Строка формы заведения: название части договора и её файл. */
export type FileDraft = { key: string; title: string; file: File | null };

/**
 * Почему набор файлов заведения ещё не готов.
 *
 * Правило то же, что у набора версии на сервере (`versionGap` в
 * `places.model.ts`), только раньше: там место без файла, здесь строка без
 * файла или без названия. Названия здесь и заводятся, поэтому добавилось
 * своё — повтор: два места с одним названием сервер не примет, и узнать об
 * этом после заведения договора человеку незачем.
 *
 * Пустая строка значит, что набор готов.
 */
export function fileRowsNote(rows: FileDraft[]): string {
  if (!rows.length || !rows[0].file) return 'Нужен файл договора';
  const noTitle = rows.find((r) => !r.title.trim());
  if (noTitle) return 'У каждого файла своё название: назовите его';
  const noFile = rows.find((r) => !r.file);
  if (noFile) return `Нет файла для «${noFile.title.trim()}»`;
  const seen = new Set<string>();
  for (const r of rows) {
    const key = r.title.trim().toLowerCase();
    if (seen.has(key)) return `Название «${r.title.trim()}» повторяется: у каждой части договора своё`;
    seen.add(key);
  }
  return '';
}

export const STATUS_LABEL: Record<ContractStatus, string> = {
  DRAFT: 'Черновик',
  COMMENTING: 'Комментирование',
  EDITING: 'Правки у держателя',
  VISAS: 'На визировании',
  UK_SIGNING: 'Подпись УК',
  SIGNING: 'На подписи',
  SCAN: 'Ожидает скан',
  SIGNED: 'Подписан',
  CANCELLED: 'Отменён',
};

/** Режим УК словами (часть 4): «Внутри» — гостевая ссылка, «Снаружи» — держатель шлёт версии сам. */
export const UK_MODE_LABEL: Record<'INSIDE' | 'OUTSIDE', string> = {
  INSIDE: 'Внутри',
  OUTSIDE: 'Снаружи',
};

/**
 * Почему на этой странице сейчас нельзя писать замечания.
 *
 * Вкладка комментирования живёт всё время, от заведения до подписи, и между
 * кругами она читаемая, но неподвижная. Человеку нужно с одного взгляда
 * отличить «круг закрыт» от «что-то сломалось», поэтому причина называется
 * словами, а не пустым местом и не отсутствием кнопок. Пустая строка значит,
 * что круг идёт и писать можно.
 */
export function commentingNote(status: ContractStatus, round: number, mayWrite: boolean): string {
  if (status === 'COMMENTING') {
    return mayWrite
      ? ''
      : `Идёт круг ${round}. Замечания пишет тот, кто назначен на этап «Комментировать договор».`;
  }
  const why: Record<Exclude<ContractStatus, 'COMMENTING'>, string> = {
    DRAFT: 'Комментирование ещё не открыто: договор в черновике.',
    EDITING: `Круг ${round} закрыт: держатель вносит правки. Следующий круг откроет новая версия.`,
    VISAS: 'Версия на визировании: текст заморожен до решения виз.',
    UK_SIGNING: 'Договор на подписи у УК: текст заморожен.',
    SIGNING: 'Договор на подписи: текст заморожен.',
    SCAN: 'Договор подписан, ждём скан.',
    SIGNED: 'Договор подписан: круги закончены.',
    CANCELLED: 'Договор отменён: круги закончены.',
  };
  return `${why[status]} Замечания читаются, новых не пишут.`;
}

/**
 * Что человек читает рядом с кольцом хода, пока файл уезжает (задача Ильи
 * 22.09.2026).
 *
 * Ожидание тут длиннее самой отправки: сервер разбирает docx и делает из него
 * PDF для предпросмотра, и это ещё несколько секунд после того, как байты
 * кончились. Кольцо всё это время стоит полным, а слово меняется — иначе
 * заполненная дуга читалась бы как «готово», а окно продолжало бы ждать.
 */
export const uploadNote = (share: number): string =>
  share < 1 ? `Отправляю ${Math.round(share * 100)} %` : 'Готовлю предпросмотр';

/** Исполнитель этапа глазами объяснения: имя, очередь должности и состояние. */
export type StageDoer = { userName: string; positionName: string; state: string };

/**
 * Почему у договора нет требований вовсе: путь пройден или брошен.
 *
 * Во всяком рабочем статусе требование есть хотя бы одно (`RULES` в
 * `server/src/contracts/contracts.model.ts`), и раздел показывает его вместе
 * с тем, кого ждут. Пусто бывает только на концах пути — там и говорится, что
 * это конец, а не поломка.
 */
const END_NOTE: Partial<Record<ContractStatus, string>> = {
  SIGNED: 'Договор подписан: путь пройден, требований у него больше нет.',
  CANCELLED: 'Договор отменён: требований у него больше нет.',
};

export const idleNote = (status: ContractStatus): string => END_NOTE[status] ?? '';

/*
 * ---------------------------------------------------------------------------
 * Раздел «Доступные действия» процессной вкладки (дизайн-система 3.60).
 *
 * Раздел — не ряд кнопок, а перечень требований (решение Ильи 22.09.2026).
 * Требование это то, без чего процесс не двинется дальше. Способов закрыть
 * требование может быть несколько — это ветки одного подраздела, а не разные
 * подразделы: «загрузить следующую версию» и «отправить эту на визирование»
 * закрывают один и тот же круг. А вот способ подписания и визы — два
 * требования: выполнив одно, человек меняет его состояние, и второе остаётся
 * ждать.
 *
 * Список требований выводится, а не пишется руками: его считает сервер из той
 * же таблицы RULES, что раздаёт кнопки (`needsFor`), и присылает в карточке
 * (`needs`). Здесь к каждому требованию привязано только то, о чём раздел
 * говорит словами. Ключ один и тот же: `Record<ContractNeed, …>` не даёт
 * завести требование и забыть, как оно называется, а действие без требования
 * в перечень не попадает вовсе — оно ход не задерживает.
 * ---------------------------------------------------------------------------
 */

/**
 * Действия, ставшие кнопками ряда. Разрешает их сервер (`actions`), а здесь
 * отсеивается то, у чего кнопки на карточке нет вовсе:
 *
 * — отправка собранной версии живёт внутри окна сборки, своей кнопки в ряду
 *   у неё нет;
 * — способ подписания спрашивает своё право на этап: ролью договора он не
 *   выражается, потому что заданием не падает;
 * — пригласить можно всегда, когда сервер разрешает, а закрыть доступ —
 *   только открытый.
 *
 * Своих правил экран после этого не заводит: кнопки расходятся по требованиям
 * тем списком, который прислал сервер.
 */
export function shownActions(
  c: {
    actions: ContractAction[];
    guest: { open: boolean };
    /** УК в согласовании (часть 4): приглашённые от УК, режим «внутри». */
    uk?: { guest: { open: boolean } | null } | null;
  },
  maySignWay: boolean,
): ContractAction[] {
  const guest = guestButtons(c.actions, c.guest.open);
  const ukGuest = ukGuestButtons(c.actions, c.uk?.guest?.open ?? false);
  return c.actions.filter((a) => {
    if (a === 'sendVersion') return false;
    if (a === 'signWay') return maySignWay;
    if (a === 'openGuest') return guest.invite;
    if (a === 'closeGuest') return guest.close;
    if (a === 'openUkGuest') return ukGuest.invite;
    if (a === 'closeUkGuest') return ukGuest.close;
    return true;
  });
}

/** Способ подписания словами. */
export const SIGNING_LABEL: Record<string, string> = { PAPER: 'Бумага', DIADOC: 'Диадок' };

/** Виза глазами раздела: чей это шаг, чем он кончился и кто её ведёт. */
type PanelVisa = { title: string; doer: (StageDoer & { userId: string | null; result: string }) | null };


/** Итог визы, которым сервер помечает согласие (`VISA_OK` в contracts.model.ts). */
const VISA_OK = 'Согласовано';

/** Выдана ли виза и в силе ли она: брошенный круг сервер отдаёт снятым заданием. */
const visaOk = (v: PanelVisa): boolean => v.doer?.state === 'DONE' && v.doer.result === VISA_OK;

/** Договор глазами раздела: всё, чем требования называют себя и тех, кого ждут. */
export type NeedCard = {
  status: ContractStatus;
  round: number;
  initiator: { name: string };
  holder: StageDoer | null;
  visas: PanelVisa[];
  sign: StageDoer | null;
  scan: StageDoer | null;
  /** Способ подписания; пусто — не выбран, и с виз договор не уйдёт. */
  signing: string;
  /** Номер по бумаге; пусто — не присвоен, и с виз договор не уйдёт. */
  number: string;
  /** УК в согласовании договора фонда (часть 4); пусто — УК не участвует. */
  uk?: { replied: boolean; replies: { remarks: boolean }[] } | null;
  needs: { key: ContractNeed; acts: ContractAction[] }[];
};

/**
 * Что каждое требование говорит о себе.
 *
 * `shut` — единственное место, где требование объявляет себя выполненным, и
 * говорит оно это данными, а не памятью: способ выбран и номер присвоен, если
 * оба есть в карточке; визы собраны, если все три выданы и в силе. Брошенный
 * возвратом круг сюда не пролезает: сервер отдаёт визы брошенного круга
 * снятыми заданиями (`visasDropped`), и `visaOk` их не считает.
 *
 * У требований, которые закрываются только выходом из статуса, `shut` пуст
 * всегда: пока договор стоит здесь, они не выполнены по определению.
 */
export const NEED_META: Record<
  ContractNeed,
  {
    title: (c: NeedCard) => string;
    shut: (c: NeedCard) => string;
  }
> = {
  out: {
    title: () => 'Договор уходит подрядчику',
    shut: () => '',
  },
  ukReply: {
    title: (c) => `УК ответила по кругу ${c.round}`,
    shut: (c) =>
      c.uk?.replied ? `Ответ получен: ${c.uk.replies[0].remarks ? 'с замечаниями' : 'без замечаний'}.` : '',
  },
  round: {
    title: (c) => `Круг ${c.round}: текст идёт дальше`,
    shut: () => '',
  },
  signWay: {
    title: () => 'Способ подписания выбран, номер присвоен',
    shut: (c) =>
      c.signing && c.number ? `Способ: ${SIGNING_LABEL[c.signing] ?? c.signing}. Номер: ${c.number}.` : '',
  },
  visas: {
    title: () => 'Визы собраны',
    shut: (c) => (c.visas.length && c.visas.every(visaOk) ? 'Все визы собраны.' : ''),
  },
  ukSign: {
    title: () => 'Скан, подписанный УК, приложен',
    shut: () => '',
  },
  sign: {
    title: () => 'Договор подписан',
    shut: () => '',
  },
  scan: {
    title: () => 'Скан подписанной бумаги приложен',
    shut: () => '',
  },
};

/**
 * Состояние требования. Три, и они отвечают на один вопрос — кто и что должен
 * сделать:
 *
 * — `mine` — ждут вас, и вот чем это делается: ветки стоят кнопками здесь же;
 * — `theirs` — ждут не вас, и раздел называет кого;
 * — `done` — требование выполнено, договор ждёт остальных.
 */
export type NeedState = 'mine' | 'theirs' | 'done';

/** Требование так, как его показывает раздел. */
export type PanelNeed = {
  key: ContractNeed;
  /**
   * Номер требования в перечне, с единицы. Не хранится и не пишется руками:
   * это место строки в том самом порядке хода, которым их прислал сервер.
   * Им же требование зовётся в строке ожидания («Ожидается выполнение
   * действия 2»), поэтому разойтись номеру и ожиданию негде.
   */
  num: number;
  title: string;
  state: NeedState;
  /** Ветки: кнопки, которыми этот человек требование закроет. */
  acts: ContractAction[];
};

/** Что раздел показывает целиком. */
export type ActionsPanel = {
  needs: PanelNeed[];
  /** Сколько требований выполнено и сколько их всего. */
  done: number;
  total: number;
  /**
   * Чего ждут дальше: первое невыполненное требование по номеру, а когда
   * таких нет — что всё сделано. Считается из того же перечня, что и номера
   * со счётом.
   */
  awaits: string;
  /**
   * Номер требования, у которого эта строка стоит, — последнего выполненного;
   * 0 — выполненных нет, и говорить «ожидается» не у чего. Строка одна на
   * раздел и стоит у нижнего из выполненных: повтори её у каждого, раздел
   * трижды сказал бы одно и то же, а у верхнего она отрывается от того
   * требования, о котором говорит.
   */
  awaitsAt: number;
  /** Работа вместо кнопки: нажимать нечего, потому что действие и есть работа. */
  work: { lead: string; hint: string } | null;
  /** Кнопки, которые ход договора не задерживают: отмена, номер, ссылка. */
  free: ContractAction[];
  /** Почему требований нет вовсе; пусто, пока они есть. */
  idle: string;
};

/** Подпись состояния и тон её чипа: оба выводятся, а не выбираются в разметке. */
export const NEED_STATE: Record<NeedState, { label: string; tone: '' | 'active' | 'ok' }> = {
  mine: { label: 'Ваш ход', tone: 'active' },
  theirs: { label: 'Ожидает', tone: '' },
  done: { label: 'Выполнено', tone: 'ok' },
};

/**
 * Что раздел «Доступные действия» говорит этому человеку.
 *
 * Перечень требований приходит с сервера в порядке хода. Состояние каждого
 * считается по одному правилу: выполнено — так и сказано; есть своя ветка —
 * ваш ход; нет ветки — ждут другого, и он назван.
 */
export function actionsPanel(
  c: NeedCard,
  now: { shown: ContractAction[]; mayComment: boolean; userId: string },
): ActionsPanel {
  const needs: PanelNeed[] = c.needs.map((row, i) => {
    const meta = NEED_META[row.key];
    const num = i + 1;
    const title = meta.title(c);
    const acts = row.acts.filter((a) => now.shown.includes(a));
    const shut = meta.shut(c);
    if (shut) return { key: row.key, num, title, state: 'done', acts };
    if (acts.length) return { key: row.key, num, title, state: 'mine', acts };
    return { key: row.key, num, title, state: 'theirs', acts: [] };
  });
  /*
   * Комментирование ход не задерживает: круг закрывает держатель, и ждать
   * замечаний договор не обязан. Но человеку, которому оно открыто, делать
   * есть что, а кнопки у этого нет — замечание пишется в самом тексте.
   */
  const work =
    now.mayComment && c.status === 'COMMENTING'
      ? {
          lead: 'Вам доступно комментирование документа',
          hint: 'Выделите место в тексте ниже и напишите замечание.',
        }
      : null;
  // Что не входит ни в одно требование, то ход и не задерживает: отмена,
  // номер руками после виз, ссылка подрядчику. Второго списка для этого не
  // нужно — хватает того, что прислал сервер.
  const held = new Set(c.needs.flatMap((row) => row.acts));
  /*
   * Счёт, номера и строка ожидания считаются здесь и из одного перечня, а не
   * собираются разметкой по месту: разойдись они — раздел сказал бы «выполнено
   * 1 из 2» и ждал бы требования, которого в перечне нет.
   */
  const shutRows = needs.filter((n) => n.state === 'done');
  const done = shutRows.length;
  const next = needs.find((n) => n.state !== 'done');
  return {
    needs,
    done,
    total: needs.length,
    awaits: next ? `Ожидается выполнение действия ${next.num}` : 'Все обязательные действия выполнены',
    awaitsAt: done ? shutRows[done - 1].num : 0,
    work,
    free: now.shown.filter((a) => !held.has(a)),
    idle: needs.length ? '' : idleNote(c.status),
  };
}

/** Сколько требований ждёт этого человека: это число носит вкладка (3.9). */
export const stepCount = (needs: PanelNeed[]): number => needs.filter((n) => n.state === 'mine').length;

/**
 * Какой вкладкой открывается договор, когда вкладка не названа (решение Ильи
 * 22.09.2026: «если договор находится в стадии согласования — согласование;
 * если уже согласован — данные»).
 *
 * «В стадии согласования» — это весь путь до подписи: черновик,
 * комментирование, правки, визы, подпись и ожидание скана. Пока путь идёт,
 * человек приходит за вопросом «где оно и что дальше», и ответ на него живёт
 * на процессной вкладке (3.60).
 *
 * Подписанный уходит к данным: делать на процессной вкладке больше нечего,
 * там Empty «Сейчас действий нет», а смотрят в подписанном договоре суммы,
 * сроки и реквизиты. Отменённый — туда же и по той же причине: его путь не
 * кончился, а брошен, и требований у него не осталось ни одного. Третьего
 * ответа у вопроса нет: статусов восемь, и каждый попадает в одну из двух
 * половин.
 *
 * Названная вкладка этим правилом не перебивается: правило отвечает только
 * там, где вкладку не назвали (`tab` слоя пуст). Поэтому ссылка на
 * «Платежи» открывает платежи и у подписанного, и у идущего договора, а
 * «назад» возвращает на тот слой, с которого пришли.
 */
export const landingTab = (status: ContractStatus): ContractTab =>
  status === 'SIGNED' || status === 'CANCELLED' ? 'terms' : 'approval';

/** Тон берётся из карты статусов дизайн-системы: красный это проблема. */
export const STATUS_TONE: Record<ContractStatus, '' | 'active' | 'ok' | 'warn' | 'crit'> = {
  DRAFT: '',
  COMMENTING: 'active',
  EDITING: 'active',
  VISAS: 'active',
  UK_SIGNING: 'active',
  SIGNING: 'warn',
  SCAN: 'warn',
  SIGNED: 'ok',
  CANCELLED: 'crit',
};

/** Сколько договор стоит на месте: «сегодня», «3 дня». */
export const daysLabel = (n: number): string => (n > 0 ? `${n} ${plural(n, 'день', 'дня', 'дней')}` : 'сегодня');

/**
 * Версия договора глазами подписи: круг, день загрузки и признак скана.
 * Признака нет у гостевого списка версий — скан подрядчику не показывается
 * вовсе: ссылка гаснет в тот же миг, когда скан прикладывают.
 */
export type VersionRef = {
  id: string;
  /** Место версии; у подписанного скана пусто — он вне круга версий. */
  placeId?: string;
  round: number;
  createdAt: string;
  signed?: boolean;
};

const VDT = (iso: string): string =>
  new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });

/**
 * Номер версии — её круг, а не место в списке. У подрядчика список обрезан
 * рубежом ссылки, и порядковый номер назвал бы третью версию первой — сам же
 * гость при этом читает в шапке «Круг 3». Круг 0 бывает только у черновика,
 * где версия одна: она первая.
 */
export function versionNo(versions: VersionRef[], id: string): number {
  const v = versions.find((x) => x.id === id);
  return v ? v.round || 1 : 0;
}

/**
 * Подпись версии: «Версия 2 · 18.09». Одна на оба экрана — зону документа и
 * сравнение версий: пока сравнение считало версии по местам в списке, один и
 * тот же файл назывался на двух экранах по-разному.
 *
 * Подписанный скан зовётся сканом, а не версией: он хранится кругом
 * подписи, то есть тем же кругом, что и последняя рабочая версия, и два
 * пункта списка читались одинаково — «Версия 3», различить только по дате.
 * Скан не круг правок, а итог, поэтому у него своя подпись.
 */
export function versionLabel(versions: VersionRef[], id: string): string {
  const v = versions.find((x) => x.id === id);
  if (!v) return '';
  return v.signed
    ? `Подписанный скан · ${VDT(v.createdAt)}`
    : `Версия ${versionNo(versions, id)} · ${VDT(v.createdAt)}`;
}

/*
 * Лента документа переключается кругами, а не файлами: версия это набор мест,
 * и в круге их несколько. Пункт переключателя — круг правок целиком либо
 * подписанный скан, который кругом не является и стоит особняком.
 */
export type DocEntry = {
  key: string;
  label: string;
  round: number;
  /** Подписанный скан: у пункта один файл и нет мест версии. */
  scanId: string;
  /** Есть ли что показать: хотя бы у одного файла круга есть предпросмотр. */
  hasPdf: boolean;
};

/** Один документ ленты: место, его название и файл этого места в круге. */
export type DocItem = { placeId: string; title: string; versionId: string; hasPdf: boolean };

/**
 * Пункты переключателя: круги по порядку, потом подписанные сканы. Круг
 * называется версией и своим номером — тем же, что в подписи файла, — а скан
 * зовётся сканом: он не круг правок, а итог.
 */
export function docEntries(versions: (VersionRef & { hasPdf: boolean })[]): DocEntry[] {
  const out: DocEntry[] = [];
  const rounds = [...new Set(versions.filter((v) => !v.signed).map((v) => v.round))].sort((a, b) => a - b);
  for (const round of rounds) {
    const files = versions.filter((v) => !v.signed && v.round === round);
    out.push({
      key: `r${round}`,
      label: `Версия ${round || 1} · ${VDT(files[0].createdAt)}`,
      round,
      scanId: '',
      hasPdf: files.some((v) => v.hasPdf),
    });
  }
  for (const scan of versions.filter((v) => v.signed)) {
    out.push({
      key: `s${scan.id}`,
      label: `Подписанный скан · ${VDT(scan.createdAt)}`,
      round: scan.round,
      scanId: scan.id,
      hasPdf: true,
    });
  }
  return out;
}

/**
 * Документы выбранного пункта, по одному на место и в порядке мест. У скана
 * мест нет вовсе: он один файл и показывается один.
 *
 * Места, файла которых в круге не оказалось, в ленту не идут: такое бывает у
 * договоров, заведённых до того, как версия стала набором мест, и рисовать им
 * пустой лист незачем.
 */
export function docFeed(
  places: { id: string; title: string }[],
  versions: (VersionRef & { hasPdf: boolean })[],
  entry: DocEntry | null,
): DocItem[] {
  if (!entry) return [];
  if (entry.scanId) {
    const scan = versions.find((v) => v.id === entry.scanId);
    return scan ? [{ placeId: '', title: 'Подписанный скан', versionId: scan.id, hasPdf: true }] : [];
  }
  const out: DocItem[] = [];
  for (const place of places) {
    const file = versions.find((v) => !v.signed && v.round === entry.round && v.placeId === place.id);
    if (file) out.push({ placeId: place.id, title: place.title, versionId: file.id, hasPdf: file.hasPdf });
  }
  return out;
}

/**
 * Предупреждение панели: показан не последний круг, потому что у последнего
 * нет предпросмотра. Замечание уходит к версии на экране, и без этой строки
 * разговор молча шёл бы по вытесненному тексту.
 */
export function previewGapNote(entries: DocEntry[], shownKey: string): string {
  // Скан в счёт не идёт ни с одной стороны: он не круг правок, а итог, и
  // предпросмотра у него нет по устройству — сам он и есть PDF. Без этого
  // подписанный договор встречал строкой «Показана версия 3: у версии 3 нет
  // предпросмотра» — обе про один и тот же круг.
  const rounds = entries.filter((e) => !e.scanId);
  const shown = rounds.find((e) => e.key === shownKey);
  if (!shown) return '';
  const last = rounds[rounds.length - 1];
  if (!last || last.key === shownKey || last.hasPdf) return '';
  return `Показана версия ${shown.round || 1}: у версии ${last.round || 1} нет предпросмотра`;
}

/**
 * Предупреждение в таблице версий: преобразование в PDF не удалось, показывать
 * в зоне документа нечего, и замечания идут к предыдущей версии.
 *
 * Подписанный скан сюда не попадает. У него `hasPdf` тоже пуст, но по другой
 * причине: скан приезжает готовым PDF и кладётся в `data`, а колонка `pdf`
 * хранит только преобразованный docx (`scan()` в
 * server/src/contracts/contracts.service.ts). Показать скан есть чем — он
 * открывается по ссылке, — и чип «без предпросмотра» на нём просто врал.
 */
export const previewMissing = (v: { hasPdf: boolean; signed?: boolean }): boolean => !v.signed && !v.hasPdf;

/**
 * С чего открывается сравнение: два последних круга правок. Скан из списков
 * не убран — человек вправе взять его сам, — но по умолчанию не встаёт: у
 * него нет разбора текста, и экран открывался бы пустым сравнением с собой
 * же. Пустой `from` значит, что рабочая версия одна и сравнивать нечего.
 */
export function compareDefaults(versions: VersionRef[]): { from: string; to: string } {
  const rounds = versions.filter((v) => !v.signed);
  return {
    from: rounds.length > 1 ? rounds[rounds.length - 2].id : '',
    to: rounds.length > 1 ? rounds[rounds.length - 1].id : '',
  };
}

/**
 * Почему сравнение не собралось. У подписанного скана разбора текста нет по
 * устройству: он приходит готовым PDF, абзацы из него не вынимаются. Это не
 * поломка, и сказать надо именно так, а не «нет разбора или слишком велик».
 */
export function compareGapNote(versions: VersionRef[], from: string, to: string): string {
  const scan = [from, to].some((id) => versions.find((v) => v.id === id)?.signed);
  return scan
    ? 'Подписанный скан не сравнивается: это готовый PDF без разбора текста'
    : 'Сравнение недоступно: у версии нет разбора текста или он слишком велик';
}

// ---------- заведение договора ----------

/*
 * Ниже — правила формы заведения. Каждое формулируется предложением, поэтому
 * живёт функцией с тестом, а не строкой внутри разметки.
 */

/*
 * Копия TYPE_RULES (server/src/contractors/contract-types.model.ts): форма
 * обязана говорить то же, что сервер. Таблицы разложены по полям, как их
 * читает экран; литеральные копии сверяются тестом на общих примерах.
 */

/** Тип договора словами (`label`). */
export const TYPE_LABEL: Record<ContractType, string> = {
  BUILD: 'Строительный подряд',
  PIR: 'Подряд на проектные и изыскательские работы',
  SUPPLY: 'Поставка',
  SERVICES: 'Возмездное оказание услуг',
  MACHINERY: 'Аренда техники с экипажем',
  PROPERTY: 'Аренда имущества',
  CARRIAGE: 'Перевозка',
  AGENCY: 'Агентский',
  DESIGN_BUILD: 'Проектирование и строительство',
  SALE: 'Купля-продажа',
  LICENSE: 'Лицензионный',
  NDA: 'Соглашение о конфиденциальности (NDA)',
  LOAN: 'Заём',
  ASSIGNMENT: 'Уступка права требования',
  GIFT: 'Дарение',
  INSURANCE: 'Страхование',
  TERM_SHEET: 'Соглашение о намерениях',
  INVESTMENT: 'Инвестиционное соглашение',
  OTHER: 'Иной',
};

/**
 * Нужна ли по видам работ направления производственная проверка (`capability`):
 * у аренды техники, аренды имущества, агентского и новых типов её нет.
 */
export const TYPE_CAPABILITY: Record<ContractType, boolean> = {
  BUILD: true,
  PIR: true,
  SUPPLY: true,
  SERVICES: true,
  MACHINERY: false,
  PROPERTY: false,
  CARRIAGE: true,
  AGENCY: false,
  DESIGN_BUILD: true,
  SALE: false,
  LICENSE: false,
  NDA: false,
  LOAN: false,
  ASSIGNMENT: false,
  GIFT: false,
  INSURANCE: false,
  TERM_SHEET: false,
  INVESTMENT: false,
  OTHER: false,
};

/** Название документа с маленькой буквы (`docName`): «договор займа». */
export const TYPE_DOC_NAME: Record<ContractType, string> = {
  BUILD: 'договор строительного подряда',
  PIR: 'договор подряда на проектные и изыскательские работы',
  SUPPLY: 'договор поставки',
  SERVICES: 'договор возмездного оказания услуг',
  MACHINERY: 'договор аренды техники с экипажем',
  PROPERTY: 'договор аренды имущества',
  CARRIAGE: 'договор перевозки',
  AGENCY: 'договор агентирования',
  DESIGN_BUILD: 'договор на проектирование и строительство',
  SALE: 'договор купли-продажи',
  LICENSE: 'лицензионный договор',
  NDA: 'соглашение о конфиденциальности',
  LOAN: 'договор займа',
  ASSIGNMENT: 'договор уступки права требования',
  GIFT: 'договор дарения',
  INSURANCE: 'договор страхования',
  TERM_SHEET: 'соглашение о намерениях',
  INVESTMENT: 'инвестиционное соглашение',
  OTHER: 'договор',
};

/** Набор закрывающих: те же одиннадцать значений, что и у сервера. */
export type ClosingSet =
  | 'KS'
  | 'WORKS_ACT'
  | 'SERVICES_ACT'
  | 'PIR_ACT'
  | 'SUPPLY'
  | 'MACHINERY'
  | 'PROPERTY'
  | 'CARRIAGE'
  | 'AGENT'
  | 'TRANSFER'
  | 'DESIGN_BUILD';

export const CLOSING_LABEL: Record<ClosingSet, string> = {
  KS: 'КС-2 и КС-3',
  WORKS_ACT: 'акт выполненных работ или УПД',
  SERVICES_ACT: 'акт оказанных услуг или УПД',
  PIR_ACT: 'акт сдачи-приёмки и накладная на документацию',
  SUPPLY: 'УПД или ТОРГ-12',
  MACHINERY: 'акт или УПД, путевые листы и рапорты',
  PROPERTY: 'акты передачи и возврата, акт или УПД за период',
  CARRIAGE: 'транспортная накладная, акт или УПД',
  AGENT: 'отчёт агента и акт',
  TRANSFER: 'акт приёма-передачи или УПД',
  DESIGN_BUILD: 'по предметам: у ПИР — акт сдачи-приёмки и накладная, у стройки — КС-2 и КС-3 или акт',
};

/** Допустимые наборы закрывающих по типу; первый — по умолчанию, пусто — закрывающих нет. */
export const TYPE_CLOSING: Record<ContractType, ClosingSet[]> = {
  BUILD: ['KS', 'WORKS_ACT'],
  PIR: ['PIR_ACT'],
  SUPPLY: ['SUPPLY'],
  SERVICES: ['SERVICES_ACT', 'WORKS_ACT', 'KS'],
  MACHINERY: ['MACHINERY'],
  PROPERTY: ['PROPERTY'],
  CARRIAGE: ['CARRIAGE'],
  AGENCY: ['AGENT'],
  DESIGN_BUILD: ['KS', 'WORKS_ACT'],
  SALE: ['TRANSFER', 'SUPPLY'],
  LICENSE: [],
  NDA: [],
  LOAN: [],
  ASSIGNMENT: [],
  GIFT: [],
  INSURANCE: [],
  TERM_SHEET: [],
  INVESTMENT: [],
  OTHER: [],
};

/** Чем документ выступает, когда выбирает закрывающие (копия `ClosingRole` в contract-types.model.ts). */
export type ClosingRole = 'CONTRACT' | 'FRAME' | 'WORK' | 'CHANGE';

/**
 * Закрывающие: та же логика, что у сервера (`closingNote` в
 * contract-types.model.ts). Нужна пакету рамочного — каждая ДС пакета
 * выбирает закрывающие сама, и набор проверяется как у самостоятельного
 * заведения.
 */
export function closingNote(
  type: ContractType,
  role: ClosingRole,
  closing: ClosingSet | '' | undefined,
  purpose: Purpose = 'BUY',
): string {
  const picked = closing ?? '';
  if (role === 'FRAME') {
    return picked ? 'У рамочного договора своих закрывающих нет: их выбирают в каждом ДС на предмет' : '';
  }
  if (role === 'CHANGE') {
    return picked ? 'ДС об изменении закрывающих не меняет: они остаются теми, что выбраны при заведении' : '';
  }
  const allowed = purpose === 'OTHER' ? [] : TYPE_CLOSING[type];
  if (!allowed.length) {
    return picked ? `У договора типа «${TYPE_LABEL[type]}» закрывающих нет` : '';
  }
  if (!picked) return 'Нужно выбрать закрывающие документы';
  if ((allowed as string[]).includes(picked)) return '';
  return `${TYPE_LABEL[type]} закрывается так: ${allowed.map((c) => CLOSING_LABEL[c]).join('; ')}`;
}

/** Назначение словами (`PURPOSE_LABEL` в server/src/contracts/purpose.model.ts). */
export const PURPOSE_LABEL: Record<Purpose, string> = {
  BUY: 'Закупка',
  SELL: 'Продажа',
  OTHER: 'Прочее',
};

/** Поля формы заведения по типу договора. */
export type TypeForm = {
  /** Назначения, с которыми бывает договор этого типа. */
  purposes: Purpose[];
  /** Названия сторон: та, что платит, и та, что исполняет. null — без направления: «Сторона 1», «Сторона 2». */
  sides: { payer: string; performer: string } | null;
  /** Тип — направление классификатора: у закупки предмет выбирается видом работ. */
  direction: boolean;
  /** Только между нашими компаниями (`internal` в server/src/contractors/contract-types.model.ts). */
  internal?: true;
  /** Больше не заводится новым договором (решение Ильи 30.09.2026): старые договоры и их ДС читаются как были. */
  retired?: true;
  /** Рамочный возможен (только у закупки по виду работ — решает `shapeOf`). */
  frame: boolean;
  /** Сумма разового: твёрдая, потолок или её нет вовсе. */
  price: 'SUM' | 'CEILING' | 'NONE';
  calendar: boolean;
  /** Подпись календаря: «Платёжный календарь» или «График погашения». */
  calendarLabel: string;
  /** Допустимые виды платежей календаря. */
  payKinds: PaymentKind[];
  project: boolean;
  attachment: string;
  /**
   * Как зовётся ДС на предмет; `null` — ДС на новый предмет не бывает.
   * `'ДС'` — общая подпись, остальные значения — своя по типу (решение Ильи
   * 28.09.2026): «ДС на работы», «ДС на услуги», «ДС на поставку».
   */
  child: 'ДС' | 'ДС на работы' | 'ДС на услуги' | 'ДС на поставку' | null;
  /** Индекс в номере: `…/ДС-3`. Старые `…/СП-3` (до 28.09.2026) не переписываются. */
  childCode: 'ДС';
};

export const TYPE_FORM: Record<ContractType, TypeForm> = {
  BUILD: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'заказчик', performer: 'подрядчик' },
    direction: true,
    frame: true,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL', 'RETENTION'],
    project: true,
    attachment: '',
    child: 'ДС на работы',
    childCode: 'ДС',
  },
  PIR: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'заказчик', performer: 'подрядчик' },
    direction: true,
    frame: true,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL', 'RETENTION'],
    project: true,
    attachment: '',
    child: 'ДС на работы',
    childCode: 'ДС',
  },
  SUPPLY: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'покупатель', performer: 'поставщик' },
    direction: true,
    frame: true,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: 'ДС на поставку',
    childCode: 'ДС',
  },
  SERVICES: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'заказчик', performer: 'исполнитель' },
    direction: true,
    frame: true,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: 'ДС на услуги',
    childCode: 'ДС',
  },
  MACHINERY: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'арендатор', performer: 'арендодатель' },
    direction: true,
    frame: false,
    price: 'CEILING',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: 'Ставки',
    child: 'ДС',
    childCode: 'ДС',
  },
  PROPERTY: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'арендатор', performer: 'арендодатель' },
    direction: true,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: 'ДС',
    childCode: 'ДС',
  },
  CARRIAGE: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'грузоотправитель', performer: 'перевозчик' },
    direction: true,
    frame: false,
    price: 'CEILING',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: 'Тарифы',
    child: 'ДС',
    childCode: 'ДС',
  },
  AGENCY: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'принципал', performer: 'агент' },
    direction: true,
    frame: false,
    price: 'CEILING',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: 'ДС',
    childCode: 'ДС',
  },
  DESIGN_BUILD: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'заказчик', performer: 'подрядчик' },
    direction: false,
    internal: true,
    retired: true,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL', 'RETENTION'],
    project: true,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  SALE: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'покупатель', performer: 'продавец' },
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: 'ДС',
    childCode: 'ДС',
  },
  LICENSE: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'лицензиат', performer: 'лицензиар' },
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  NDA: {
    purposes: ['OTHER'],
    sides: null,
    direction: false,
    frame: false,
    price: 'NONE',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: [],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  LOAN: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'заёмщик', performer: 'займодавец' },
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'График погашения',
    payKinds: ['INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  ASSIGNMENT: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'цессионарий', performer: 'цедент' },
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  GIFT: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'одаряемый', performer: 'даритель' },
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: [],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  INSURANCE: {
    purposes: ['BUY', 'SELL'],
    sides: { payer: 'страхователь', performer: 'страховщик' },
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: true,
    calendarLabel: 'Платёжный календарь',
    payKinds: ['ADVANCE', 'INTERIM', 'FINAL'],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  TERM_SHEET: {
    purposes: ['OTHER'],
    sides: null,
    direction: false,
    frame: false,
    price: 'NONE',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: [],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  INVESTMENT: {
    purposes: ['OTHER'],
    sides: null,
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: [],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
  OTHER: {
    purposes: ['OTHER'],
    sides: null,
    direction: false,
    frame: false,
    price: 'SUM',
    calendar: false,
    calendarLabel: 'Платёжный календарь',
    payKinds: [],
    project: false,
    attachment: '',
    child: null,
    childCode: 'ДС',
  },
};

/** Типы, которые предлагаются новому договору: все, кроме снятых с заведения (`retired`). */
export const OFFERED_TYPES = (Object.keys(TYPE_FORM) as ContractType[]).filter((t) => !TYPE_FORM[t].retired);

/** Налоговый режим подрядчика: закрытый список из спеки. */
export const TAX_MODES = ['OSN', 'USN', 'AUSN', 'ESHN', 'PSN', 'NPD'] as const;

export type TaxMode = (typeof TAX_MODES)[number];

export const TAX_MODE_LABEL: Record<TaxMode, string> = {
  OSN: 'Общий режим',
  USN: 'Упрощённая система',
  AUSN: 'Автоматизированная упрощённая система',
  ESHN: 'Единый сельскохозяйственный налог',
  PSN: 'Патентная система',
  NPD: 'Налог на профессиональный доход',
};

/** Ставки НДС закрытым списком — те же, что принимает сервер. */
export const VAT_RATES = [0, 5, 7, 10, 22];

/** Подпись ставки: ноль означает «без НДС», а не ставку в ноль процентов. */
export const vatRateLabel = (rate: number): string => (rate > 0 ? `${rate} %` : 'Без НДС');

/** Валюты договора: рубль по умолчанию, остальные знаком. */
export const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'];

const CURRENCY_SIGN: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€', CNY: '¥' };

const WHOLE = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const WITH_KOPECKS = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Деньги как их набрали: запятая — тот же разделитель, что и точка. Русская
 * раскладка даёт запятую сама, и `Number('100,5')` это `NaN` — отказал бы
 * сервер, когда файл уже загружен. Пробелы разрядов уходят здесь же: в поле
 * лежит «1 200 000», а числом это «1200000». Замена одна на весь экран: и
 * сумма договора, и платежи идут через неё, и через неё же считается остаток.
 */
export const moneyText = (text: string): string =>
  String(text ?? '')
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(',', '.');

/** Разделитель разрядов — неразрывный пробел: число не рвётся переносом. */
const NBSP = '\u00a0';

/** Знаки, которые в поле денег что-то значат: цифры и разделитель дробной части. */
const SIGNIFICANT = /[\d.,]/;

/**
 * Деньги в поле ввода: разряды появляются прямо при наборе, «1200000»
 * читается как «1 200 000». Всё лишнее выбрасывается — знак валюты и пробелы
 * из вставленного куска не должны спорить с набором, а дробная часть всегда
 * запятой и не длиннее копейки.
 *
 * Ноль перед числом не копится: «05» это «5», но одинокий «0» остаётся —
 * с него начинается «0,5».
 */
export function moneyMask(text: string): string {
  const raw = String(text ?? '');
  let whole = '';
  let cents = '';
  let dot = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      if (dot) cents = (cents + ch).slice(0, 2);
      else whole += ch;
    } else if ((ch === ',' || ch === '.') && !dot) {
      dot = true;
    }
  }
  whole = whole.replace(/^0+(?=\d)/, '');
  const groups = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  if (!dot) return groups;
  return `${groups || '0'},${cents}`;
}

/**
 * Сколько значащих знаков стоит левее курсора. По этому числу курсор и
 * возвращается на место после маски: пробел разрядов появился или исчез, а
 * человек остался там же, где печатал.
 */
export function moneyCaretFrom(text: string, pos: number): number {
  let n = 0;
  for (const ch of String(text ?? '').slice(0, pos)) if (SIGNIFICANT.test(ch)) n += 1;
  return n;
}

/** Где в готовой строке стоит курсор после стольких значащих знаков. */
export function moneyCaretTo(masked: string, count: number): number {
  if (count <= 0) return 0;
  let n = 0;
  for (let i = 0; i < masked.length; i += 1) {
    if (SIGNIFICANT.test(masked[i])) n += 1;
    if (n === count) return i + 1;
  }
  return masked.length;
}

/**
 * Деньги с разрядами в валюте договора: «1 250 000 $», «18,30 ₽».
 *
 * Копейки показываются, только когда они есть: у круглой суммы два нуля после
 * запятой — шум. Но если копейки есть, их ровно две, а не «18,3»: деньги на
 * сервере целые копейки, и хвост в один знак читается как другая сумма.
 * Округление до копейки здесь же, чтобы двоичная дробь не решала, что человек
 * прочтёт.
 */
export const moneyIn = (amount: number, currency: string): string => {
  const sign = CURRENCY_SIGN[currency] ?? currency;
  if (!Number.isFinite(amount)) return `${amount} ${sign}`;
  const kop = Math.round(amount * 100);
  const fmt = kop % 100 === 0 ? WHOLE : WITH_KOPECKS;
  return `${fmt.format(kop / 100)} ${sign}`;
};

/**
 * Налог, сидящий внутри суммы с НДС: выделяется по ставке и округляется до
 * копейки. Нулевая ставка означает «без НДС» — сумма и есть цена, налога в
 * ней нет. Копия правила сервера (`vatOf` в intake.model.ts): считается
 * целыми копейками, потому что двоичная дробь тут врёт.
 */
export function vatOf(amount: number, rate: number): number {
  if (rate <= 0 || !Number.isFinite(amount)) return 0;
  const kop = Math.round(amount * 100);
  return Math.round((kop * rate) / (100 + rate)) / 100;
}

/**
 * Подпись поля суммы: смысл суммы задаёт ставка. Есть НДС — вводится сумма с
 * НДС, нет НДС — цена без него. Пишется сумма без НДС (`net`) — цена без
 * НДС независимо от ставки (решение Ильи 28.09.2026).
 */
export const amountTitle = (rate: number, net = false): string =>
  net ? 'Сумма без НДС' : rate > 0 ? 'Сумма с НДС' : 'Цена без НДС';

/** Подпись графы календаря платежей: тот же смысл, что у amountTitle. */
export const paymentAmountTitle = (rate: number, net = false): string =>
  net ? 'Платёж без НДС' : rate > 0 ? 'Сумма с НДС' : 'Сумма без НДС';

/**
 * Сколько налога сидит в введённой сумме — подсказка под полем. Пишется
 * сумма без НДС (`net`) — введена цена без него, подсказка называет и налог,
 * и сумму с ним.
 */
export function vatNote(amount: string, rate: number, currency: string, net = false): string {
  const n = Number(moneyText(amount));
  if (rate <= 0 || !Number.isFinite(n) || n <= 0) return '';
  if (net) {
    const gross = grossOf(n, rate);
    return `НДС ${rate} % — ${moneyIn(rub(kop(gross) - kop(n)), currency)}, с НДС — ${moneyIn(gross, currency)}`;
  }
  return `В сумме НДС ${rate} %: ${moneyIn(vatOf(n, rate), currency)}`;
}

/**
 * Пишется ли сумма нового документа ценой без НДС (решение Ильи 28.09.2026).
 * Копия серверного правила (`writesNet` в server/src/contracts/intake.model.ts)
 * слово в слово: форма обязана решать так же, как решит сервер, до отправки.
 */
export function writesNet(kind: AmendKindName | null, base: { vatIncluded: boolean } | null): boolean {
  if (kind !== 'CHANGE') return true;
  return base ? !base.vatIncluded : true;
}

/*
 * Налоги в семье договора (решение Ильи 25.09.2026). Копия правил сервера
 * (server/src/contracts/intake.model.ts) слово в слово — от «Цена без НДС» до
 * `regimeOn`; тест сервера сверяет текст обеих копий. Сумма хранится как её
 * ввели, со ставкой документа; цена без НДС считается там, где нужна.
 */

/** Рубли в целые копейки и обратно: те же имена, что у сервера, чтобы копия читалась слово в слово. */
const kop = (amount: number): number => Math.round(amount * 100);
const rub = (kopecks: number): number => kopecks / 100;

/** Цена без НДС: сумма без выделенного налога (`vatOf`), копейками. */
export function netOf(amount: number, rate: number): number {
  return rub(kop(amount) - kop(vatOf(amount, rate)));
}

/** Сумма с НДС: цена без НДС плюс налог по ставке, округлённый до копейки. */
export function grossOf(net: number, rate: number): number {
  if (rate <= 0) return net;
  return rub(kop(net) + Math.round((kop(net) * rate) / 100));
}

/** Налоговый режим с даты: `from` — день «ГГГГ-ММ-ДД», с которого он действует; у исходного пусто. */
export type TaxRegime = { mode: string; rate: number; from: string | null };

/** ДС об изменении договора глазами налоговой истории. */
export type TaxStep = {
  /** Номер в семье: подписанные одним днём разбираются по нему. */
  no: number;
  /** Какие условия оно объявило изменёнными. */
  fields: string[];
  /** День подписания; `null` — не подписано, и тогда оно не меняет ничего. */
  signedOn: string | null;
  mode: string;
  rate: number;
};

/**
 * Налоговая история договора: исходный режим и те, что открыли его
 * подписанные ДС об изменении, объявившие режим или ставку НДС, — каждый с
 * дня подписания. Задним числом ничего не меняется: до этого дня действует
 * прежний режим. История нигде не хранится, она читается из договора и его
 * ДС. Режим, объявленный тем же, строки не заводит; подписанные одним днём
 * дают один день — слово последнего по номеру.
 */
export function taxHistory(base: { mode: string; rate: number }, steps: TaxStep[]): TaxRegime[] {
  const out: TaxRegime[] = [{ mode: base.mode, rate: base.rate, from: null }];
  const signed = steps
    .filter((s) => s.signedOn !== null && (s.fields.includes('taxMode') || s.fields.includes('vatRate')))
    .sort((a, b) => (a.signedOn! < b.signedOn! ? -1 : a.signedOn! > b.signedOn! ? 1 : a.no - b.no));
  for (const s of signed) {
    const last = out[out.length - 1];
    const next: TaxRegime = {
      mode: s.fields.includes('taxMode') ? s.mode : last.mode,
      rate: s.fields.includes('vatRate') ? s.rate : last.rate,
      from: s.signedOn,
    };
    if (last.from === next.from) out.pop();
    const prev = out[out.length - 1];
    if (prev.mode === next.mode && prev.rate === next.rate) continue;
    out.push(next);
  }
  return out;
}

/** Режим на день «ГГГГ-ММ-ДД»: последний, открытый не позже этого дня. */
export function regimeOn(history: TaxRegime[], day: string): TaxRegime {
  let at = history[0];
  for (const regime of history) if (regime.from === null || regime.from <= day) at = regime;
  return at;
}

/** День «ГГГГ-ММ-ДД» по Москве: сутки договора — московские, а не UTC и не часовой пояс браузера. */
export function moscowDay(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/**
 * Налоговая история строкой: «Упрощённая система · без НДС до 12.10.2026,
 * Общий режим · 22 % с 12.10.2026». Один режим — прежняя строка режима.
 */
export function taxHistoryLine(history: TaxRegime[], at: string | null): string {
  if (history.length < 2) return taxModeLine(history[0]?.mode ?? '', at);
  return history
    .map((r, i) => {
      const what = `${TAX_MODE_LABEL[r.mode as TaxMode] ?? (r.mode || 'Режим не указан')} · ${r.rate > 0 ? `${r.rate} %` : 'без НДС'}`;
      const next = history[i + 1];
      return next ? `${what}${r.from ? ` с ${DAY(r.from)}` : ''} до ${DAY(next.from!)}` : `${what} с ${DAY(r.from!)}`;
    })
    .join(', ');
}

/**
 * Ставка, которую подсказывает режим подрядчика: общий режим платит НДС по
 * основной ставке, спецрежимы по умолчанию не платят. Это подсказка, а не
 * правило: с 2026 года упрощенец с выручкой выше порога тоже платит НДС.
 */
export const suggestedVatRate = (mode: string): number => (mode === 'OSN' ? 22 : 0);

/**
 * Ставка, которую подсказывает карточка подрядчика: своя ставка, названная
 * человеком (`card.rate`) для того же режима, что выбран сейчас, — или
 * подсказка по режиму (`suggestedVatRate`), если ставки нет или сменился
 * режим (решение Ильи 28.09.2026: ставка карточки известна только для
 * режима, на который её назвали, а не вообще).
 */
export function suggestRate(mode: string, card: { mode: string; rate: number | null }): number {
  return card.rate !== null && mode === card.mode ? card.rate : suggestedVatRate(mode);
}

/**
 * Расхождение режима и ставки: показывается предупреждением, а не запретом.
 * Режим не выбран — говорить не о чем.
 */
export function vatModeNote(mode: string, rate: number): string {
  if (!mode) return '';
  if (mode === 'OSN' && rate === 0) return 'Общий режим без НДС: проверьте основание освобождения';
  if (mode !== 'OSN' && rate > 0) return 'Спецрежим со ставкой НДС: проверьте, что подрядчик платит НДС';
  return '';
}

/**
 * Срок у договора один — дата, до которой он действует. Та же формулировка,
 * что и у `datesOk` сервера, чтобы форма не обещала того, что сервер отклонит.
 */
export const datesNote = (validTo: string): string => (validTo ? '' : 'Нужен срок действия договора');

/**
 * Работы выбранного юрлица: договор заводится от стороны работы, а не от
 * работы. Стороны работы (`sides`) считает сервер (`projectSides`,
 * server/src/projects/sides.model.ts): у ООО — одна, у ЗПИФ — фонд-заказчик и
 * генподрядчик.
 */
export const projectsOf = (projects: WorkOption[], legalEntityId: string): WorkOption[] =>
  legalEntityId ? projects.filter((p) => p.sides.includes(legalEntityId)) : [];

/** Аренда и продажа помещений — от фонда, остальное — от генподрядчика (решение 19). */
export const PREMISES_TYPES: ContractType[] = ['SALE', 'PROPERTY'];

/** Наша сторона по умолчанию в форме договора при выборе проекта с фондом; генподрядчик — исполнитель работы. */
export function defaultSide(p: WorkOption, type: ContractType | null): string {
  if (p.fundId && type && PREMISES_TYPES.includes(type)) return p.fundId;
  return p.performer?.id ?? '';
}

/**
 * Что подставить в поле проекта: один проект подставляется сам и остаётся
 * видимым, из нескольких человек выбирает, без проектов договор заводится
 * без проекта.
 */
export const soleProject = (projects: WorkOption[]): string =>
  projects.length === 1 ? projects[0].id : '';

/** Можно ли завести договор на этот вид работ и почему нельзя. */
export type SubjectGate = { can: boolean; state: CategoryAdmission['state']; note: string };

/**
 * Допуск по предмету глазами формы.
 *
 * Подрядчик, не заявивший ни одного вида работ, идёт за бумажной частью —
 * то же исключение, что и у серверного шлюза: производственной проверки у
 * него не будет никогда. Заявленный вид работ судится своей строкой допуска.
 * Не заявленный подрядчиком вид работ не выбирается: проверять его нечем, и
 * сказать об этом честнее, чем спрятать пункт из списка.
 */
export function subjectGate(admissions: CategoryAdmission[], categoryId: string): SubjectGate {
  if (!admissions.length) return { can: true, state: 'ALLOWED', note: '' };
  const row = admissions.find((a) => a.categoryId === categoryId);
  if (!row) return { can: false, state: 'WAITING', note: 'Подрядчик не заявил этот вид работ' };
  return { can: row.state === 'ALLOWED', state: row.state, note: row.note };
}

/**
 * Допуск по направлению у рамочного договора: ему довольно одного допущенного
 * вида работ внутри направления, потому что конкретной работы он не обещает —
 * каждое допсоглашение потом проверяется своим видом работ.
 */
export function frameworkGate(
  admissions: CategoryAdmission[],
  worksInside: { id: string }[],
): SubjectGate {
  if (!admissions.length) return { can: true, state: 'ALLOWED', note: '' };
  const inside = worksInside
    .map((w) => admissions.find((a) => a.categoryId === w.id))
    .filter((a): a is CategoryAdmission => !!a);
  if (!inside.length) {
    return { can: false, state: 'WAITING', note: 'В этом направлении у подрядчика не заявлено ни одного вида работ' };
  }
  const allowed = inside.find((a) => a.state === 'ALLOWED');
  if (allowed) return { can: true, state: 'ALLOWED', note: '' };
  const waiting = inside.find((a) => a.state === 'WAITING');
  const worst = waiting ?? inside[0];
  return { can: false, state: worst.state, note: worst.note };
}

// ---------- календарь платежей ----------

/*
 * Платежи — не украшение формы: из них потом собирается платёжный календарь,
 * поэтому у каждого точная дата, а не ориентировочная, и одно из четырёх
 * назначений. Гарантийное удержание выражается платежом, а не процентом в
 * шапке договора (решение Ильи 19.09.2026): вниз по течению важно движение
 * денег, а не доля.
 */

/** Назначение платежа: четыре значения, и других не бывает. */
export const PAYMENT_KINDS: PaymentKind[] = ['ADVANCE', 'INTERIM', 'FINAL', 'RETENTION'];

export const PAYMENT_KIND_LABEL: Record<PaymentKind, string> = {
  ADVANCE: 'Аванс',
  INTERIM: 'Промежуточный',
  FINAL: 'Окончательный',
  RETENTION: 'Возврат гарантийного удержания',
};

/** Строка календаря на форме; `key` держит строку при удалении соседней. */
/** `regular` — строка поставлена действием «Регулярный платёж» (спека 2026-10-07). */
export type PaymentDraft = { key: string; amount: string; date: string; kind: PaymentKind; regular?: boolean };

/**
 * Деньги строкой в целые копейки; NaN — это не число. Копейки, а не рубли:
 * двоичная дробь решала бы сходимость календаря по остатку в сотую копейки, а
 * сервер (`paymentsBalance` в server/src/contracts/intake.model.ts) считает
 * целыми.
 */
function kopecks(text: string): number {
  const t = moneyText(text);
  if (!t) return NaN;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

/** Нетронутая строка: ни суммы, ни даты. Такая на сервер не уходит. */
export const paymentEmpty = (p: PaymentDraft): boolean => !p.amount.trim() && !p.date;

/** Что уходит с формы: нетронутые строки не уходят, начатые уходят как есть. */
export const filledPayments = (list: PaymentDraft[]): PaymentDraft[] => list.filter((p) => !paymentEmpty(p));

/** Остаток календаря: знак, тон светофора и подпись. */
export type PaymentsRest = { rest: number; ok: boolean; tone: '' | 'ok' | 'warn' | 'crit'; note: string };

/**
 * Остаток словами и тоном. Одно правило на форму и на карточку: остаток,
 * посчитанный на форме, и остаток, присланный сервером, обязаны читаться
 * одинаково — иначе человек, поправивший календарь, не узнает своего договора.
 * Остаток приходит целыми копейками, чтобы сравнение с нулём было точным;
 * недобор ещё добирается, перебор уже ошибка, и тон у них разный.
 */
export function restNote(rest: number, currency: string): Omit<PaymentsRest, 'rest'> {
  if (rest > 0) return { ok: false, tone: 'warn', note: `Не разнесено ${moneyIn(rest / 100, currency)}` };
  if (rest < 0) return { ok: false, tone: 'crit', note: `Перебор ${moneyIn(-rest / 100, currency)}` };
  return { ok: true, tone: 'ok', note: 'Платежи сходятся с суммой договора' };
}

/**
 * Сходимость платежей глазами формы. Остаток виден всегда, в том числе
 * отрицательный.
 */
export function paymentsRest(amount: string, list: PaymentDraft[], currency: string): PaymentsRest {
  const whole = kopecks(amount);
  if (Number.isNaN(whole)) return { rest: NaN, ok: false, tone: '', note: 'Сумма договора ещё не указана' };
  const planned = filledPayments(list).reduce((sum, p) => {
    const k = kopecks(p.amount);
    return sum + (Number.isNaN(k) ? 0 : k);
  }, 0);
  const rest = whole - planned;
  return { rest, ...restNote(rest, currency) };
}

/**
 * Тот же остаток на карточке заведённого договора: сервер присылает его
 * рублями (`paymentsRest` в server/src/contracts/contracts.view.ts), карточка
 * переводит в копейки и называет теми же словами, что и форма. Считать остаток
 * на экране заново значило бы вывести правило второй раз.
 */
export function cardPaymentsRest(rest: string, currency: string): PaymentsRest {
  const k = kopecks(rest);
  if (Number.isNaN(k)) return { rest: NaN, ok: false, tone: '', note: 'Остаток неизвестен' };
  return { rest: k, ...restNote(k, currency) };
}

/**
 * Первая беда в строках календаря словами: те же отказы, что у сервера
 * (`paymentRows` в server/src/contracts/contracts.service.ts), сказанные до
 * отправки. Даты при заведении не в прошлом — сегодняшняя годится, вчерашняя
 * нет; правило подписания (платёж не раньше даты подписи) здесь не при чём.
 */
export function paymentRowsNote(list: PaymentDraft[], today: string): string {
  for (let i = 0; i < list.length; i += 1) {
    const p = list[i];
    if (paymentEmpty(p)) continue;
    const where = `Платёж ${i + 1}`;
    const k = kopecks(p.amount);
    if (Number.isNaN(k) || k <= 0) return `${where}: сумма больше нуля`;
    if (!p.date) return `${where}: нужна точная дата`;
    if (p.date < today) return `${where}: дата в прошлом`;
  }
  return '';
}

/**
 * Чем начинается очередная строка: первая — окончательным платежом, следующие
 * — промежуточными. Один платёж по окончании работ и есть обычный договор,
 * остальное человек ставит сам.
 */
export const nextPaymentKind = (rows: number): PaymentKind => (rows ? 'INTERIM' : 'FINAL');

/*
 * Регулярный платёж (решение Ильи 25.09.2026): способ заполнить календарь
 * абонентского договора — сумма, периодичность и дата первого платежа, а
 * серия идёт до срока действия. Строки выходят обычные, их правят руками.
 * Серия узнаётся по самому календарю — хвост платежей одной суммы с ровным
 * шагом, — и ДС об изменении, продлевающее срок, дописывает её до нового.
 */

export type Period = 'MONTH' | 'QUARTER' | 'YEAR';

export const PERIODS: Period[] = ['MONTH', 'QUARTER', 'YEAR'];

export const PERIOD_LABEL: Record<Period, string> = {
  MONTH: 'Ежемесячно',
  QUARTER: 'Ежеквартально',
  YEAR: 'Ежегодно',
};

/** Шаг серии в месяцах. */
export const PERIOD_MONTHS: Record<Period, number> = { MONTH: 1, QUARTER: 3, YEAR: 12 };

/**
 * День через `n` месяцев от первого платежа: число то же, в коротком месяце —
 * последний день месяца. Считается от первого платежа, а не от соседа: иначе
 * тридцать первое, споткнувшись о февраль, так и осталось бы двадцать восьмым.
 */
export function monthsAfter(first: string, n: number): string {
  const [y, m, d] = first.split('-').map(Number);
  const at = y * 12 + (m - 1) + n;
  const year = Math.floor(at / 12);
  const month = at % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const two = (v: number) => String(v).padStart(2, '0');
  return `${year}-${two(month + 1)}-${two(Math.min(d, last))}`;
}

/** Даты серии от первой до срока действия включительно; `skip` первых пропускается. */
export function seriesDates(first: string, months: number, until: string, skip = 0): string[] {
  if (!first || !until || months <= 0) return [];
  const out: string[] = [];
  for (let i = skip; ; i += 1) {
    const day = monthsAfter(first, i * months);
    if (day > until) return out;
    out.push(day);
  }
}

/** Платёж серии: сумма строкой, как в поле, и дата. */
export type SeriesRow = { amount: string; date: string; kind: PaymentKind };

/**
 * Серия регулярного платежа до срока действия: промежуточные платежи одной
 * суммы. Итог — их сумма, копейками.
 */
export function regularPayments(
  amount: string,
  period: Period,
  first: string,
  until: string,
): { rows: SeriesRow[]; total: number } {
  const k = kopecks(amount);
  if (Number.isNaN(k) || k <= 0) return { rows: [], total: 0 };
  const rows = seriesDates(first, PERIOD_MONTHS[period], until).map((date) => ({
    amount: moneyMask(moneyText(amount)),
    date,
    kind: 'INTERIM' as PaymentKind,
  }));
  return { rows, total: (k * rows.length) / 100 };
}

/** Хвост календаря, который читается серией: сумма, шаг, первый платёж хвоста и сколько их. */
export type Series = { amount: string; months: number; first: string; count: number };

/**
 * Серия в хвосте календаря: не меньше трёх последних платежей одной суммы с
 * ровным шагом — месяц, квартал или год — от первого из них. Два платежа
 * серией не считаются: аванс и окончательный одной суммы — ещё не абонентский
 * договор. Из нескольких шагов побеждает самый длинный хвост.
 */
export function seriesTail(rows: SeriesRow[]): Series | null {
  const list = rows.filter((p) => p.amount.trim() && p.date);
  let best: Series | null = null;
  for (const months of [1, 3, 12]) {
    for (let count = list.length; count >= 3; count -= 1) {
      const tail = list.slice(-count);
      const k = kopecks(tail[0].amount);
      const even = tail.every(
        (p, i) => kopecks(p.amount) === k && p.date === monthsAfter(tail[0].date, i * months),
      );
      if (!even) continue;
      if (!best || count > best.count) {
        best = { amount: tail[0].amount, months, first: tail[0].date, count };
      }
      break;
    }
  }
  return best;
}

/**
 * Платежи, которыми серия продлевается до нового срока: того же размера и
 * шага, промежуточные — окончательный платёж серии остаётся тем, чем был.
 */
export function extendSeries(series: Series, until: string): SeriesRow[] {
  return seriesDates(series.first, series.months, until, series.count).map((date) => ({
    amount: series.amount,
    date,
    kind: 'INTERIM' as PaymentKind,
  }));
}

/**
 * Почему договор не уйдёт на согласование: та же фраза, которой откажет сервер
 * (`send` в server/src/contracts/contracts.service.ts). У рамочного договора
 * календаря нет, и отказывать не в чем. Пустая строка — уйдёт.
 *
 * Этой же фразой запирается и «Сохранить черновик»: платежи правятся только
 * при заведении, маршрута их правки на сервере нет вовсе, и черновик с
 * несошедшимся календарём стал бы мёртвым — отправить нельзя, поправить негде.
 */
export function paymentsSendNote(framework: boolean, amount: string, list: PaymentDraft[], currency: string): string {
  if (framework) return '';
  const balance = paymentsRest(amount, list, currency);
  if (balance.ok) return '';
  return Number.isNaN(balance.rest)
    ? balance.note
    : `Платежи не сходятся с суммой договора: ${balance.note.toLowerCase()}`;
}

// ---------- карточка договора ----------

/*
 * Ниже — правила показа карточки. Те же предложения, что и на форме, но
 * сказанные о заведённом договоре: карточка ничего не считает сама, она
 * читает ответ сервера и называет его словами.
 */

/** День без часов: даты договора, платежа и снимка пишутся одинаково. */
export const DAY = (iso: string): string => new Date(iso).toLocaleDateString('ru-RU');

/**
 * Чья форма словами. Одно место на оба экрана: карточка говорит про
 * заведённый договор, форма заведения — про наследованную у договора семьи.
 */
export const FORM_LABEL: Record<string, string> = { OURS: 'Наша', THEIRS: 'Подрядчика' };

/**
 * Календарь одной строкой: сколько платежей и на сколько денег. Им ДС об
 * изменении называет то, что несёт прежним, — перечислять чужие платежи оно
 * не обязано.
 */
export function paymentsLine(payments: { amount: string }[], currency: string): string {
  if (!payments.length) return 'Нет';
  const total = payments.reduce((sum, p) => sum + Math.round(Number(moneyText(p.amount)) * 100), 0);
  const n = payments.length;
  return `${n} ${plural(n, 'платёж', 'платежа', 'платежей')} на ${moneyIn(total / 100, currency)}`;
}

/** Валюты словами: в сумме хорош знак, а в отдельном поле нужен язык. */
export const CURRENCY_LABEL: Record<string, string> = {
  RUB: 'Рубль',
  USD: 'Доллар США',
  EUR: 'Евро',
  CNY: 'Юань',
};

export const currencyLabel = (code: string): string => CURRENCY_LABEL[code] ?? code;

/**
 * Предмет с его направлением: «Стройка · Кровля». У рамочного договора предмет
 * и есть направление, родителя у него нет, и точка ни к чему.
 */
export const subjectPath = (category: { name: string; parentName: string }): string =>
  category.parentName ? `${category.parentName} · ${category.name}` : category.name;

/**
 * Сумма договора в его валюте и с признаком налога. Смысл суммы задаёт ставка,
 * а не отдельное поле: есть НДС — это сумма с НДС, нет — цена без него.
 */
export const amountLine = (amount: string, currency: string, rate: number): string =>
  `${moneyIn(Number(amount), currency)} ${rate > 0 ? 'с НДС' : 'без НДС'}`;

/**
 * Налог, сидящий в сумме: ставка и сколько это денег. Выделенный налог считает
 * сервер (`vatOf` в intake.model.ts, целыми копейками), карточка его называет.
 */
export const vatLine = (vat: string, rate: number, currency: string): string =>
  rate > 0 ? `${rate} % · ${moneyIn(Number(vat), currency)}` : 'Без НДС';

/**
 * Налоговый режим подрядчика и дата, на которую он известен.
 *
 * Дата здесь значит ровно одно — «по состоянию на», — откуда бы режим ни
 * приехал. Из карточки подрядчика он приходит с датой публикации набора
 * налоговой: набор выходит двадцать пятого числа по состоянию на первое, и
 * день получения отчёта тут не при чём. Названный руками режим приходит с
 * датой, на которую его называют. Договор хранит снимок: изменение внутри
 * договора карточку подрядчика не трогает.
 *
 * Без даты остаются только договоры, заведённые до того, как режим переехал в
 * карточку: у новых дата обязательна вместе с режимом.
 */
export function taxModeLine(mode: string, at: string | null): string {
  if (!mode) return 'Не указан';
  const label = TAX_MODE_LABEL[mode as TaxMode] ?? mode;
  return at ? `${label} · по состоянию на ${DAY(at)}` : `${label} · без даты`;
}

/** Статусы, в которых номер ещё впереди: его присваивают на визах. */
const NUMBER_AHEAD: readonly ContractStatus[] = ['DRAFT', 'COMMENTING', 'EDITING', 'VISAS'];

/**
 * Номер с бумаги и дата договора одной строкой: они ходят парой. Внутренний
 * номер есть с заведения, а этот присваивают позже — на визах, шагом способа
 * подписания (решение Ильи 02.10.2026). До виз включительно его обещают;
 * ушедший с виз без номера, подписанный или отменённый — «Без номера».
 */
export function paperLine(number: string, date: string | null, status: ContractStatus): string {
  if (!number) return NUMBER_AHEAD.includes(status) ? 'Номер присвоят при выборе способа подписания' : 'Без номера';
  return date ? `${number} от ${DAY(date)}` : number;
}

/**
 * Срок договора: дата, до которой он действует. У рамочного он был и раньше,
 * у обычного договора и у допсоглашения на его месте стоял срок выполнения
 * работ — две даты, которые ничего не двигали (решение Ильи 21.09.2026).
 */
export const termLine = (validTo: string | null): string => (validTo ? `до ${DAY(validTo)}` : 'Не указан');

/**
 * Строка под предметом: тип договора, у продажи и прочего — с назначением
 * впереди, у внутригруппового — с пометкой. У закупки — тип, как раньше.
 */
function rowSub(r: ContractRow): string {
  const head = r.purpose === 'BUY' ? r.kindLabel : [PURPOSE_LABEL[r.purpose], r.kindLabel].filter(Boolean).join(' · ');
  return r.intraGroup ? `${head} · внутригрупповой` : head;
}

/**
 * Договоры подрядчика строками списка. Допсоглашений здесь нет (решение Ильи
 * 25.09.2026): они живут на вкладке допсоглашений своего договора, а список
 * подрядчика отвечает на вопрос «какие договоры с ним заключены».
 */
export function contractTree(rows: ContractRow[]): ContractTableRow[] {
  return rows
    .filter((r) => !r.parentId)
    .map((r) => ({
      id: r.id,
      number: r.number,
      type: contractTypeLabel(r.framework),
      subject: r.subject,
      sub: rowSub(r),
      legal: r.legalName,
      legalId: r.legalEntityId,
      project: r.project ? r.project.name : null,
      amount: r.amount,
      vatIncluded: r.vatIncluded,
      vatRate: r.vatRate,
      currency: r.currency,
      status: r.status,
      statusLabel: r.statusLabel,
      party: r.party.name,
    }));
}

/**
 * Деньги со знаком: разница между действующей и исходной суммой предмета.
 * Плюс стоит всегда — «30 ₽» в столбце изменений читалось бы как сумма, а не
 * прибавка; ноль без знака. Минус — типографский, как у сервера
 * (`moneyLabel`). Считается копейками: двоичный хвост дал бы «−0 ₽».
 */
export function signedMoney(amount: number, currency: string): string {
  const kop = Math.round(amount * 100);
  if (kop === 0) return moneyIn(0, currency);
  return `${kop > 0 ? '+' : '−'}${moneyIn(Math.abs(kop) / 100, currency)}`;
}

/** Изменённое условие в строке изменения: коротко, строчными. */
export const CHANGE_WORD: Record<AmendField, string> = {
  amount: 'сумма',
  vatRate: 'ставка НДС',
  taxMode: 'налоговый режим',
  validTo: 'срок',
  payments: 'календарь',
};

/**
 * Строка ДС об изменении: что изменено и у чего (решение Ильи 25.09.2026) —
 * «изменение ДС № 1: сумма, календарь». Изменение без объявленных условий
 * меняет формулировки в тексте — так и названо, а не пустотой.
 */
export function changeLine(what: string, changes: string[]): string {
  const words = changes.map((f) => CHANGE_WORD[f as AmendField] ?? f);
  return `изменение ${what}: ${words.length ? words.join(', ') : 'формулировки'}`;
}

/**
 * Семья договора двумя разделами вкладки (решение Ильи 25.09.2026).
 *
 * «Предметы» — ДС на новый предмет, под каждым
 * вложенным списком 3.62 его изменения на любой глубине. «Изменения» — ДС об
 * изменении самого договора. Что под чем, решает основание цепочки, которое
 * приносит сервер (`base`): изменение изменения висит на том же предмете и
 * называет его, а не промежуточное звено. Изменение, чьё основание не
 * предмет этого списка (сам договор, кольцо ссылок у старых данных), стоит в
 * «Изменениях».
 *
 * На карточке самого ДС на новый предмет список несёт только его изменения,
 * и предмета в нём нет: карточка называет себя (`selfId`), и её изменения
 * тоже называют её номером, а не «договор».
 *
 * Строка предмета несёт три суммы: действующую в столбце и под ней исходную и
 * изменения со знаком. Строка изменения показывает сумму, только если
 * изменяла её.
 */
export function familySections(
  amendments: ContractAmendRow[],
  currency: string,
  withLimit: boolean,
  type: ContractType | null,
  /** Карточка ДС на новый предмет, чья это семья; у договора пусто. */
  selfId = '',
): { subjects: ContractTableRow[]; changes: ContractTableRow[] } {
  const byNo = (x: ContractAmendRow, y: ContractAmendRow) => x.no - y.no;
  const subjectIds = new Set(amendments.filter((a) => a.kind === 'WORK').map((a) => a.id));
  /** Предмет строки из этого списка: под ним строка и стоит. */
  const baseOf = (a: ContractAmendRow) => (a.base && subjectIds.has(a.base.id) ? a.base : null);
  /** Предмет, который строка называет: из списка или сама карточка. */
  const namedBase = (a: ContractAmendRow) => (a.base && selfId && a.base.id === selfId ? a.base : baseOf(a));
  const change = (a: ContractAmendRow): ContractTableRow => {
    const base = namedBase(a);
    return {
      id: a.id,
      number: a.number || `ДС № ${a.no}`,
      type: AMEND_KIND_LABEL.CHANGE,
      subject: changeLine(base ? `ДС № ${base.no}` : 'договора', a.changes),
      // Лимит занимают предметы и их изменения, изменение вытесняет
      // изменяемое: что из этого в счёте, считает сервер (`limitCounted`).
      sub: withLimit && baseOf(a) ? (a.counted ? 'занимает лимит' : 'не в счёт лимита') : '',
      amount: a.changes.includes('amount') ? a.amount : null,
      currency,
      status: a.status,
      statusLabel: a.statusLabel,
    };
  };
  const subjects = amendments
    .filter((a) => a.kind === 'WORK')
    .sort(byNo)
    .map((a) => ({
      id: a.id,
      number: a.number || `ДС № ${a.no}`,
      type: amendKindLabel('WORK', type),
      subject: a.subject,
      sub: [withLimit ? (a.counted ? 'занимает лимит' : 'не в счёт лимита') : '', a.vatIncluded ? '' : 'без НДС']
        .filter(Boolean)
        .join(' · '),
      amount: a.vatIncluded ? a.effective : a.effectiveNet,
      sums: {
        original: moneyIn(Number(a.amount), currency),
        delta: signedMoney(Number(a.vatIncluded ? a.effective : a.effectiveNet) - Number(a.amount), currency),
      },
      currency,
      status: a.status,
      statusLabel: a.statusLabel,
      children: amendments
        .filter((c) => c.kind === 'CHANGE' && baseOf(c)?.id === a.id)
        .sort(byNo)
        .map(change),
    }));
  const changes = amendments
    .filter((a) => a.kind !== 'WORK' && !baseOf(a))
    .sort(byNo)
    .map(change);
  return { subjects, changes };
}

/**
 * Сколько договоров подрядчика скрыто сужением по работам. Договоры видны по
 * работам, в команде которых человек состоит, или по всем работам функции при
 * охвате должности «видит все функции работы»; остальное называется числом и
 * причиной — иначе пустой список читается как поломка.
 */
export const hiddenNote = (n: number): string =>
  `${n} ${plural(n, 'договор', 'договора', 'договоров')} по работам, в команде которых вас нет. ` +
  'Их видят участники работы и должности с охватом «видит все функции работы»';

/**
 * Что закрывает договор наглухо. Это единственное, что экран решает сам:
 * чёрный список и приостановка — решение человека, и они закрывают всё.
 *
 * Общий статус компании здесь больше не спрашивается (решение 19.09.2026): он
 * стал сводкой по видам работ и договор не пропускает и не запрещает. Допуск
 * проверяется по названному предмету, и проверяет его сервер
 * (`contractGate` в server/src/contractors/admission.model.ts), а форма
 * показывает его отказ по каждому виду работ до отправки. Кнопка, запертая
 * сводкой, гасла у подрядчика, которому сервер договор бы подписал.
 */
export function contractBlocked(status: ContractorStatus): string {
  if (status === 'BLOCKED') return 'Подрядчик в чёрном списке: договор невозможен';
  if (status === 'SUSPENDED') return 'Подрядчик приостановлен: договор невозможен';
  return '';
}

/**
 * Календарь против фактической даты подписания: аванс раньше подписания не
 * бывает. Повторяет серверный `signDateNote` слово в слово, чтобы скан
 * говорил это до загрузки файла, а не после отказа.
 */
export function signDateNote(signed: string, payments: { kind: PaymentKind; date: string }[]): string {
  if (!signed) return '';
  const late = payments.filter((p) => p.date.slice(0, 10) < signed);
  if (!late.length) return '';
  const list = late.map((p) => `${PAYMENT_KIND_LABEL[p.kind].toLowerCase()} ${DAY(p.date)}`).join(', ');
  return (
    `Платежи раньше даты подписания: ${list}. Календарь правится только при заведении: ` +
    'проверьте дату договора, а если она верна — договор придётся отменить и завести заново.'
  );
}

// ---------- допсоглашения ----------

/*
 * Правила допсоглашения, сказанные формой до отправки: те же, которыми
 * откажет сервер (`limitFit` и заведение в
 * `server/src/contracts/contracts.service.ts`).
 */

/**
 * Сколько лимита занято соседями, если это допсоглашение изменяет одно из них.
 *
 * Изменение вытесняет изменяемое целиком, поэтому его сумма из занятого
 * уходит: допсоглашение на шестьсот, изменённое до девятисот, съедает
 * девятьсот, а не полторы тысячи. Вытесняется только то, что лимит и
 * занимало: изменяемое, которое уже вытеснено кем-то другим или отменено,
 * в занятое и так не входило. Лимит без НДС (решение Ильи 25.09.2026), и
 * вытесняется цена без НДС (`net`). Считается целыми копейками — рублями
 * хвост двоичной дроби решал бы, помещается ли допсоглашение.
 */
export function limitTaken(
  used: string,
  amendments: { id: string; net: string; counted: boolean }[],
  amendsId: string,
): number {
  const total = Math.round(Number(moneyText(used)) * 100);
  const row = amendsId ? amendments.find((a) => a.id === amendsId) : undefined;
  const drop = row?.counted ? Math.round(Number(moneyText(row.net)) * 100) : 0;
  return (total - drop) / 100;
}

/**
 * Помещается ли допсоглашение в лимит рамочного договора. Лимит, занятое и
 * сумма допсоглашения — без НДС. Та же фраза, что у
 * сервера (`limitFit` в server/src/contracts/intake.model.ts), и те же числа:
 * «нельзя» без чисел отправляет человека к калькулятору. Деньги печатаются
 * знаком валюты, как везде на экране, а решение принимается целыми копейками.
 * Пустая строка — помещается.
 */
export function limitNote(args: {
  limit: string;
  taken: number;
  amount: string;
  currency: string;
  parentSeq: number;
}): string {
  const kop = (v: number) => Math.round(v * 100);
  const limit = kop(Number(moneyText(args.limit)));
  const amount = kop(Number(moneyText(args.amount)));
  if (!Number.isFinite(limit) || !Number.isFinite(amount) || !args.amount.trim()) return '';
  const free = limit - kop(args.taken);
  const rest = free - amount;
  if (rest >= 0) return '';
  const m = (k: number) => moneyIn(k / 100, args.currency);
  return (
    `Допсоглашение не помещается в лимит рамочного договора № ${args.parentSeq}: свободно ${m(free)} без НДС ` +
    `из ${m(limit)}, в допсоглашении ${m(amount)} без НДС — перебор ${m(-rest)}. ` +
    'Уменьшите сумму допсоглашения или отмените то, что занимает лимит'
  );
}

/**
 * Основание цепочки изменений: ДС на новый предмет, из которого она выросла,
 * или сам договор семьи. Копия серверного `amendBase` (intake.model.ts): по
 * основанию сервер решает и границу срока, и есть ли у изменения платёжный
 * календарь, — а форма обязана решать это тем же правилом, иначе она обещает
 * то, в чём откажут.
 *
 * Идём от звена вверх по «что изменяет», пока не упрёмся в предмет или в то,
 * чего в семье нет: чего нет — это сам договор семьи. Кольцо ссылок
 * обрывается на себе, а не крутится вечно.
 */
export function amendBase(
  children: { id: string; kind: AmendKindName | ''; amends: { id: string } | null }[],
  startId: string,
): string {
  const byId = new Map(children.map((child) => [child.id, child]));
  const seen = new Set<string>();
  let at = startId;
  for (;;) {
    const link = byId.get(at);
    if (!link || link.kind !== 'CHANGE' || !link.amends) return at;
    if (seen.has(at)) return at;
    seen.add(at);
    at = link.amends.id;
  }
}

/**
 * Срок действия внутри срока действия договора семьи: то же, чем откажет
 * сервер (`underContractNote`), и теми же словами. Срока у договора может не
 * быть только у переехавшего — тогда и сверять не с чем.
 *
 * Мерка своя у каждой формы семьи (решение Ильи 24.09.2026): ею связаны ДС
 * на новый предмет и любое его изменение — у рамочного и у разового договора
 * одинаково (решение Ильи 25.09.2026). Изменение самого договора она не
 * касается: срок оно и меняет. Кого мерить, решает вызывающий по основанию
 * цепочки.
 */
export function contractTermNote(validTo: string, parentValidTo: string | null): string {
  if (!validTo || !parentValidTo) return '';
  const until = parentValidTo.slice(0, 10);
  if (validTo <= until) return '';
  return `Срок действия выходит за срок действия договора: он действует до ${DAY(parentValidTo)}`;
}

// ---------- род допсоглашения ----------

/*
 * Допсоглашение бывает двух родов, и род объявляется при заведении, а не
 * выводится из глубины связи (решение Ильи 24.09.2026; серверные правила —
 * `amendKindVerdict` и `amendFieldsVerdict` в intake.model.ts). Роды
 * называются названием бумаги (решение Ильи 25.09.2026): «ДС на новый
 * предмет» и «ДС об изменении», у рамочных типов ДС на новый предмет несёт
 * свою подпись (решение Ильи 28.09.2026) — «ДС на работы» у строительного
 * подряда и ПИР, «ДС на услуги» у услуг, «ДС на поставку» у поставки.
 * Объяснения лежат здесь, рядом с правилами, а не в разметке: экран их
 * показывает, а не сочиняет.
 */

/** Род допсоглашения: те же два значения, что принимает сервер. */
export const AMEND_KINDS = ['WORK', 'CHANGE'] as const;

export type AmendKindName = (typeof AMEND_KINDS)[number];

/** Род названием бумаги: им подписаны кнопки, чип карточки, заголовки и столбец семьи. */
export const AMEND_KIND_LABEL: Record<AmendKindName, string> = {
  WORK: 'ДС на новый предмет',
  CHANGE: 'ДС об изменении',
};

/**
 * Род с оглядкой на тип (решение Ильи 28.09.2026): «ДС на предмет» у
 * рамочных типов называется своим словом — «ДС на работы», «ДС на услуги»,
 * «ДС на поставку». `'ДС'` в `child` — сентинел общей подписи.
 */
export const amendKindLabel = (kind: AmendKindName, type: ContractType | null): string => {
  const child = type ? TYPE_FORM[type].child : null;
  return kind === 'WORK' && child && child !== 'ДС' ? child : AMEND_KIND_LABEL[kind];
};

/** Тип договора одним словом: рамочный или разовый (решение Ильи 25.09.2026). */
export const contractTypeLabel = (framework: boolean): string => (framework ? 'Рамочный' : 'Разовый');

/**
 * Название документа в шапке карточки: тип и номер, а не предмет
 * (замечание Ильи 25.09.2026 — из «Стройка» не было понятно, договор это или
 * допсоглашение и под каким номером). Номер присваивают на визах (решение
 * Ильи 02.10.2026), и до него договор называется предметом; допсоглашение —
 * местом в семье.
 */
export function docTitle(c: {
  framework: boolean;
  contractType: ContractType | null;
  number: string;
  parentId: string | null;
  amendKind: AmendKindName | '';
  amendLabel: string;
  subject: string;
}): string {
  if (c.parentId) {
    const kind = c.amendKind ? amendKindLabel(c.amendKind, c.contractType) : 'Допсоглашение';
    return `${kind} ${c.number ? `№ ${c.number}` : c.amendLabel}`.trim();
  }
  const no = c.number ? `№ ${c.number}` : `«${c.subject}»`;
  if (!c.contractType) return `${contractTypeLabel(c.framework)} договор ${no}`;
  // «Разовый» — только там, где бывает рамочный: у займа и NDA такого выбора нет.
  const prefix = c.framework ? 'Рамочный ' : TYPE_FORM[c.contractType].frame ? 'Разовый ' : '';
  const title = `${prefix}${TYPE_DOC_NAME[c.contractType]} ${no}`;
  return title.charAt(0).toUpperCase() + title.slice(1);
}

/**
 * Слово стороны договора: подрядчик — только у закупки у подрядчика с
 * карточкой, у всех прочих — вторая сторона. Винительный — для приглашения:
 * «Пригласить подрядчика», «Пригласить вторую сторону».
 */
export function partyWord(c: { purpose: Purpose; party: { profileId: string | null } }): {
  nom: string;
  acc: string;
} {
  return c.purpose === 'BUY' && c.party.profileId
    ? { nom: 'Подрядчик', acc: 'подрядчика' }
    : { nom: 'Вторая сторона', acc: 'вторую сторону' };
}

// ---------- фильтры реестра договоров ----------

/*
 * Фильтры реестра работают на экране по уже суженному списку (часть 2,
 * пункт 6): сервер присылает только видимое, и сужать дальше — дело экрана.
 * Считаются только договоры: допсоглашения живут в своём договоре.
 */

export type RegistryFilter = {
  purpose: Purpose | 'INTRA' | null;
  type: ContractType | '';
  legalEntityId: string;
  orgId: string;
  /** '' — все, 'none' — без проекта. */
  projectId: string;
  status: ContractStatus | '';
  /** Номер, предмет, контрагент. */
  q: string;
};

const roots = (rows: ContractRow[]): ContractRow[] => rows.filter((r) => !r.parentId);

/**
 * Группа договора в фильтре: внутригрупповой — своя группа (решение Ильи
 * 02.10.2026), в закупку, продажу и прочее он не входит, и группы вместе
 * дают «Все».
 */
const groupOf = (r: ContractRow): Purpose | 'INTRA' => (r.intraGroup ? 'INTRA' : r.purpose);

export function filterContracts(rows: ContractRow[], f: RegistryFilter): ContractRow[] {
  const q = f.q.trim().toLowerCase();
  return roots(rows).filter(
    (r) =>
      (f.purpose === null || groupOf(r) === f.purpose) &&
      (!f.type || r.contractType === f.type) &&
      (!f.legalEntityId || r.legalEntityId === f.legalEntityId) &&
      (!f.orgId || r.party.orgId === f.orgId) &&
      (!f.projectId || (f.projectId === 'none' ? !r.project : r.project?.id === f.projectId)) &&
      (!f.status || r.status === f.status) &&
      (!q || [r.number, r.subject, r.party.name].some((t) => t.toLowerCase().includes(q))),
  );
}

/** Счётчики вкладок назначения: всё, закупка, продажа, прочее, внутригрупповые; группы не пересекаются. */
export function purposeCounts(rows: ContractRow[]): Record<'all' | Purpose | 'INTRA', number> {
  const list = roots(rows);
  const count = (g: Purpose | 'INTRA') => list.filter((r) => groupOf(r) === g).length;
  return { all: list.length, BUY: count('BUY'), SELL: count('SELL'), OTHER: count('OTHER'), INTRA: count('INTRA') };
}

/** Значения списка без повторов, по алфавиту. */
function optionsOf(pairs: [string, string][]): DropdownOption[] {
  const seen = new Map<string, string>();
  for (const [value, label] of pairs) if (value && !seen.has(value)) seen.set(value, label);
  return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
}

/** Выборы фильтров — из того, что в реестре есть: пустых пунктов в списке нет. */
export function registryOptions(rows: ContractRow[]): {
  types: DropdownOption[];
  legals: DropdownOption[];
  parties: DropdownOption[];
  projects: DropdownOption[];
  statuses: DropdownOption[];
} {
  const list = roots(rows);
  const projects = optionsOf(list.flatMap((r) => (r.project ? [[r.project.id, r.project.name] as [string, string]] : [])));
  return {
    types: optionsOf(list.flatMap((r) => (r.contractType ? [[r.contractType, TYPE_LABEL[r.contractType]] as [string, string]] : []))),
    legals: optionsOf(list.map((r) => [r.legalEntityId, r.legalName])),
    parties: optionsOf(list.map((r) => [r.party.orgId, r.party.name])),
    projects: list.some((r) => !r.project) ? [...projects, { value: 'none', label: 'Без работы' }] : projects,
    statuses: optionsOf(list.map((r) => [r.status, r.statusLabel])),
  };
}

/**
 * Род целой фразой: что это такое и что человек сейчас будет заполнять.
 * Развилка объясняет оба рода до выбора — выбор между двумя названиями без
 * объяснения и есть та самая догадка, которой быть не должно.
 */
export const AMEND_KIND_NOTE: Record<AmendKindName, string> = {
  WORK:
    'Новый предмет по тому же договору: свой вид работ, своя сумма, свой срок в пределах договора, ' +
    'свой календарь платежей и закрывающие. Условий договора оно не трогает; у рамочного занимает часть лимита',
  CHANGE:
    'Меняет то, о чём уже договорились: сумму, ставку НДС, налоговый режим, срок действия или календарь платежей. ' +
    'Форма открывается нынешними условиями — поменяйте нужное, остальное останется прежним',
};

/**
 * Изменение рамочного договора не опускает лимит ниже занятого: опустить его
 * ниже обещанного по ДС на новый предмет значит задним числом сделать
 * обещанное непроходным.
 * Та же фраза и те же числа, что у сервера (`limitFloorNote`), сказанные до
 * отправки. Пустая строка — новый лимит занятое покрывает.
 */
export function limitFloorNote(args: { limit: string; used: string; currency: string }): string {
  const kop = (v: string) => Math.round(Number(moneyText(v)) * 100);
  const limit = kop(args.limit);
  const used = kop(args.used);
  if (!Number.isFinite(limit) || !Number.isFinite(used) || !args.limit.trim()) return '';
  const rest = limit - used;
  if (rest >= 0) return '';
  const m = (k: number) => moneyIn(k / 100, args.currency);
  return (
    `Новый лимит рамочного договора меньше занятого: занято ${m(used)}, ` +
    `в ДС об изменении ${m(limit)} — не хватает ${m(-rest)}. ` +
    'Отмените то, что занимает лимит, или оставьте сумму не меньше занятой'
  );
}

/** Условия, которые допсоглашение вправе изменить. Копия `AMENDABLE` сервера. */
export const AMENDABLE = ['amount', 'vatRate', 'taxMode', 'validTo', 'payments'] as const;

export type AmendField = (typeof AMENDABLE)[number];

/** Название изменяемого условия словами: те же слова, что у сервера. */
export const AMENDABLE_LABEL: Record<AmendField, string> = {
  amount: 'Сумма',
  vatRate: 'Ставка НДС',
  taxMode: 'Налоговый режим',
  validTo: 'Срок действия',
  payments: 'Платёжный календарь',
};

/**
 * Календарь платежей у изменения рамочного договора: его нет. Изменение
 * рамочного меняет потолок, а платят по ДС на новый предмет — изменять в нём
 * нечего. То же правило, что у сервера (`wantsCalendar`).
 */
export const amendWantsCalendar = (kind: AmendKindName, correctsFramework: boolean): boolean =>
  kind === 'WORK' || !correctsFramework;

/** «На основании допсоглашения № 3»: подпись под изменённым условием. */
export const termSourceLabel = (no: number): string => `на основании допсоглашения № ${no}`;

/**
 * Что изменяет новое ДС об изменении: последнее живое звено цепочки того, на
 * чьей карточке нажали «ДС об изменении» (решение Ильи 24.09.2026).
 *
 * Не само звено, а хвост его цепочки: два изменения одного звена сервер
 * считает двумя ветками и складывает обе (`limitCounted`), и лимит съелся бы
 * дважды. Отменённое звено хвостом не бывает — изменяют то, что перед ним.
 * Изменение самого договора семьи приходит с пустым «что изменяет»: корня в
 * ряду допсоглашений нет.
 *
 * Хвостов больше одного бывает только у старых данных, где одно звено
 * изменяли дважды: тогда выбирает человек.
 */
export function chainTails(
  amendments: { id: string; kind: AmendKindName | ''; status: ContractStatus; amends: { id: string } | null }[],
  rootId: string,
  startId: string,
): string[] {
  const edits = (id: string) =>
    amendments.filter(
      (a) => a.kind === 'CHANGE' && a.status !== 'CANCELLED' && (a.amends ? a.amends.id === id : id === rootId),
    );
  const out: string[] = [];
  const seen = new Set<string>();
  const walk = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    const next = edits(id);
    if (!next.length) out.push(id);
    for (const a of next) walk(a.id);
  };
  walk(startId);
  return out;
}

/** Условия так, как их набирают на форме. */
export type TermsDraft = {
  amount: string;
  vatRate: number;
  taxMode: string;
  taxModeAt: string;
  validTo: string;
  payments: PaymentDraft[];
};

/**
 * Условия так, как их правит форма: пишется сумма без НДС (`net`) — правится
 * цена без него, а не действующая сумма с ним. Тем же пользуется и
 * заполнение формы при открытии на правку, и `changedTerms`, чтобы сравнивать
 * поле с тем же, что в него ввели.
 */
export function enteredTerms(terms: ContractTermsView, net: boolean): ContractTermsView {
  if (!net) return terms;
  return { ...terms, amount: terms.net, payments: terms.payments.map((p) => ({ ...p, amount: p.net })) };
}

/**
 * Что ДС об изменении меняет: сравнение формы с действующими условиями
 * изменяемого, которыми она открылась. Галочек «что меняю» больше нет
 * (решение Ильи 24.09.2026): человек меняет поля, как в договоре, а
 * объявленное сервер получает этим сравнением. Режим и дата, на которую он
 * известен, — одно условие, как и у сервера. Календарь сравнивается только
 * там, где он у изменения есть.
 */
export function changedTerms(was: ContractTermsView, now: TermsDraft, calendar: boolean, net = false): AmendField[] {
  const kop = (v: string) => Math.round(Number(moneyText(v)) * 100);
  const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
  const entered = enteredTerms(was, net);
  const out: AmendField[] = [];
  if (kop(now.amount) !== kop(entered.amount)) out.push('amount');
  if (now.vatRate !== was.vatRate) out.push('vatRate');
  if (now.taxMode !== was.taxMode || now.taxModeAt !== day(was.taxModeAt)) out.push('taxMode');
  if (now.validTo !== day(was.validTo)) out.push('validTo');
  if (calendar) {
    const line = (rows: { amount: string; date: string; kind: string }[]) =>
      JSON.stringify(rows.map((p) => [kop(p.amount), day(p.date), p.kind]));
    if (line(filledPayments(now.payments)) !== line(entered.payments)) out.push('payments');
  }
  return out;
}
